import { Text } from '@mantine/core';
import { formatDateTimeWithComma } from 'src/meetings/utils/date-utils';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { FileData, UploadedFile } from '../../hooks/file-uploads/types';
import FileStatusIcon from '../components/FileStatusIcon';
import { GENERATED_DOCUMENT_TYPE_LABELS } from '../components/generatedDocumentTypes';
import { GeneratedDocument } from '../hooks/useGeneratedDocuments';
import { FilesTableColumn } from './FilesTable';

export type GeneratedDocumentRow = UploadedFile & {
  isGenerated: true;
  document: GeneratedDocument;
};

export function toGeneratedDocumentRow(doc: GeneratedDocument): GeneratedDocumentRow {
  return {
    id: `${doc.documentType}-${doc.id}`,
    file: buildRowFile(doc),
    isFolder: false,
    isResolved: true,
    createdAt: doc.createdAt,
    detectedClients: [],
    isGenerated: true,
    document: doc,
  };
}

export const generatedDocumentCreatedColumn: FilesTableColumn<GeneratedDocumentRow> = {
  label: 'Created',
  sortable: true,
  sortValueGetter: (record: GeneratedDocumentRow) => record.document.createdAt,
  valueBuilder: (record: GeneratedDocumentRow) => (
    <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.10" lh="18px">
      {formatDateTimeWithComma(record.document.createdAt)}
    </Text>
  ),
};

export const generatedDocumentStatusColumn: FilesTableColumn<GeneratedDocumentRow> = {
  label: 'Status',
  sortable: false,
  textAlign: 'center' as const,
  valueBuilder: (record: GeneratedDocumentRow) => (
    <GeneratedDocumentStatusCell doc={record.document} />
  ),
};

export const generatedDocumentTypeColumn: FilesTableColumn<GeneratedDocumentRow> = {
  label: 'Type',
  sortable: true,
  sortValueGetter: (record: GeneratedDocumentRow) =>
    GENERATED_DOCUMENT_TYPE_LABELS[record.document.documentType],
  valueBuilder: (record: GeneratedDocumentRow) => (
    <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.10" lh="18px">
      {GENERATED_DOCUMENT_TYPE_LABELS[record.document.documentType]}
    </Text>
  ),
};

function buildRowFile(doc: GeneratedDocument): FileData {
  if (doc.file) {
    return { ...doc.file, fileName: doc.displayName };
  }
  return {
    id: doc.fileId ?? 0,
    fileName: doc.displayName,
    isFolder: false,
    isResolved: true,
    isFiled: true,
    isTemporary: false,
    createdAt: doc.createdAt,
    fileSize: 0,
    fileType: '',
  };
}

function GeneratedDocumentStatusCell({ doc }: { doc: GeneratedDocument }) {
  if (doc.backgroundProcess) {
    const { status } = doc.backgroundProcess;
    return (
      <FileStatusIcon
        status={status}
        tooltipLabel={status === 'SUCCESS' ? 'Document is ready' : undefined}
        processingTooltipLabel="Generating..."
        withProcessingBackground
      />
    );
  }
  if (doc.fileId != null) {
    return <FileStatusIcon status="SUCCESS" tooltipLabel="Document is ready" />;
  }
  return <></>;
}
